/**
 * Snap settings per spread via checkpoint editor settings API.
 * `snapDistance` is the ruler-guide proximity threshold (pt), not document grid spacing.
 *
 * GET /checkpoints/{spreadId}/editorsettings/snapsettings
 * PUT /checkpoints/{spreadId}/editorsettings/snapsettings/update
 */

import { httpClient } from '@/api/http/client'
import { logger } from '@/utils/logger'

const SNAP_DISTANCE_MIN = 1
const SNAP_DISTANCE_MAX = 24
const SNAP_DISTANCE_DEFAULT = 12

function enc(value: string): string {
  return encodeURIComponent(value.trim())
}

function snapSettingsPath(spreadId: string): string {
  return `/checkpoints/${enc(spreadId)}/editorsettings/snapsettings`
}

export interface SnapSettingsPutBody {
  /** Ruler-guide snap threshold in points (1–24). */
  snapDistance: number
  snapToGuides: boolean
}

/** Values ready for Preferences UI and View → Snap To Guides */
export interface SnapSettingsLoaded {
  snapToGuides: boolean
  /** Preferences string: ruler-guide snap distance, digits 1–24 pt */
  snapDistance: string
  /** Spread-level checkpoint settings updated timestamp. */
  updatedAt?: string
}

function normalizeUpdatedAt(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()

  return trimmed === '' ? undefined : trimmed
}

function parseSnapDistanceToPt(raw: unknown): number {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return clampSnapDistancePt(Math.round(raw))
  }

  if (typeof raw === 'string') {
    const t = raw.trim()

    if (t === '') return SNAP_DISTANCE_DEFAULT
    const n = parseInt(t, 10)

    if (!Number.isFinite(n)) return SNAP_DISTANCE_DEFAULT

    return clampSnapDistancePt(n)
  }

  return SNAP_DISTANCE_DEFAULT
}

export function clampSnapDistancePt(n: number): number {
  if (!Number.isFinite(n)) return SNAP_DISTANCE_DEFAULT
  const rounded = Math.round(n)

  return Math.min(SNAP_DISTANCE_MAX, Math.max(SNAP_DISTANCE_MIN, rounded))
}

function ptToPreferenceString(pt: number): string {
  return String(clampSnapDistancePt(pt))
}

async function getSnapSettings(spreadId: string): Promise<SnapSettingsLoaded | null> {
  try {
    const dto = await httpClient.get<{
      snapDistance?: string | number
      snapToGuides?: boolean
      updatedAt?: string
    }>(snapSettingsPath(spreadId))

    if (dto == null || typeof dto !== 'object') return null

    const snapDistancePt = parseSnapDistanceToPt(dto.snapDistance)
    /** Default true: ruler-guide snapping on for legacy/missing payloads; smart-guide snap follows Show Guides in the editor. */
    const snapToGuides = typeof dto.snapToGuides === 'boolean' ? dto.snapToGuides : true

    return {
      snapToGuides,
      snapDistance: ptToPreferenceString(snapDistancePt),
      updatedAt: normalizeUpdatedAt(dto.updatedAt),
    }
  } catch (error) {
    logger.warn(`Failed to load snap settings for spread ${spreadId}:`, error)
    return null
  }
}

async function updateSnapSettings(spreadId: string, body: SnapSettingsPutBody): Promise<void> {
  const payload: SnapSettingsPutBody = {
    snapToGuides: body.snapToGuides,
    snapDistance: clampSnapDistancePt(body.snapDistance),
  }

  await httpClient.put<SnapSettingsPutBody, unknown>(
    `${snapSettingsPath(spreadId)}/update`,
    payload
  )
}

export const snapSettingsService = {
  getSnapSettings,
  updateSnapSettings,
}
